/**
 * The wiring between a `<Field>` and the control inside it.
 *
 * Separate from the component so neither module exports both a component and a
 * hook — Fast Refresh cannot hot-swap a module that does, and the form layer is
 * where a lost HMR cycle costs the most (you have just typed into six inputs).
 *
 * What travels through here is the accessibility contract: the `id` the label
 * points at, and the `aria-describedby` that connects the control to its error
 * message. Left to each screen, that is the first thing dropped — and a
 * validation message no screen reader announces may as well not exist.
 */
import { createContext, use } from 'react'

export type FieldState = {
  id: string
  /** Ids of the hint and error text, for `aria-describedby`. */
  describedBy: string | undefined
  invalid: boolean
  required: boolean
}

/** What `useFieldProps` hands to a control. */
export type FieldControlProps = {
  id?: string | undefined
  'aria-invalid'?: true | undefined
  'aria-describedby'?: string | undefined
  'aria-required'?: true | undefined
}

export const FieldContext = createContext<FieldState | null>(null)

/**
 * Accessibility props for the control inside a `<Field>`.
 *
 * The `| undefined` on each is `exactOptionalPropertyTypes` being strict about
 * the difference between "absent" and "present and undefined". Spreading these
 * onto an element needs the second, so it has to be spelled out.
 */
export function useFieldProps(): FieldControlProps {
  const field = use(FieldContext)
  if (!field) return {}

  return {
    id: field.id,
    'aria-invalid': field.invalid || undefined,
    'aria-describedby': field.describedBy,
    'aria-required': field.required || undefined,
  }
}
